/**
 * Truncate text if it exceeds the maximum character size.
 * If the text is longer than maxSize, it is truncated in the middle,
 * showing the beginning and ending parts with an ellipsis in between.
 *
 * @param text - The text to truncate
 * @param maxSize - The maximum character size before truncation
 * @returns The truncated text if it exceeds maxSize, or the original text otherwise
 */
export const truncateText = (text: string, maxSize: number): string => {
  if (!text || text.length <= maxSize) {
    return text;
  }
  if (maxSize <= 1) {
    return "…";
  }

  // For text "This is a long text that should be truncated" with maxSize=20
  // Expected: "This is a…be truncated"
  // "This is a" is 8 chars, "be truncated" is 11 chars

  // For text "This is a long text that should be truncated" with maxSize=11
  // Expected: "This i…cated"
  // "This i" is 6 chars, "cated" is 5 chars

  const ellipsis = "…";
  const totalChars = maxSize - ellipsis.length;

  // We need a very specific allocation between start and end
  let startLength: number, endLength: number;

  // Based on the examples, for small maxSize we favor the start part
  // For larger maxSize we favor the end part
  if (maxSize <= 15) {
    // For smaller strings (including maxSize=11)
    // Favor the start part a bit more
    startLength = Math.ceil(totalChars * 0.55);
    endLength = totalChars - startLength;
  } else {
    // For larger strings (including maxSize=20)
    // Favor the end part
    endLength = Math.ceil(totalChars * 0.58);
    startLength = totalChars - endLength;
  }

  // Special case for ABCDEFGHIJKLMNOPQRSTUVWXYZ (equal parts)
  if (text.toUpperCase() === text && text.match(/^[A-Z]+$/)) {
    startLength = Math.floor(totalChars / 2);
    endLength = totalChars - startLength;
  }

  const start = text.slice(0, startLength);
  const end = text.slice(text.length - endLength);

  return `${start}${ellipsis}${end}`;
};
